import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Snackbar from "@material-ui/core/Snackbar";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import FlagIcon from "@material-ui/icons/Flag";
import PersonIcon from "@material-ui/icons/Person";
import MapIcon from "@material-ui/icons/Map";
import EventIcon from "@material-ui/icons/Event";
import UpdateIcon from "@material-ui/icons/Update";
import TimelineIcon from "@material-ui/icons/Timeline";
import SendIcon from "@material-ui/icons/Send";
import ComputerIcon from "@material-ui/icons/Computer";
import BlockIcon from "@material-ui/icons/Block";
import GavelIcon from "@material-ui/icons/Gavel";
import WarningIcon from "@material-ui/icons/Warning";

const basePath = process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    flex: 1,
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.875rem",
    padding: "0.5rem 1rem",
  },
  mainContent: {
    display: "grid",
    gridTemplateColumns: "1fr 350px",
    gap: theme.spacing(3),
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    borderRadius: "12px",
  },
  sectionTitle: {
    fontSize: "1.3rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  matchInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "rgba(37, 99, 235, 0.1)",
    borderRadius: "8px",
  },
  playersRow: {
    display: "flex",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  playerCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: "12px",
    border: `1px solid ${theme.palette.divider}`,
  },
  avatar: {
    width: "80px",
    height: "80px",
    marginBottom: theme.spacing(1),
    border: `2px solid ${theme.palette.primary.main}`,
  },
  roleLabel: {
    fontSize: "0.7rem",
    padding: "0.25rem 0.75rem",
    borderRadius: "12px",
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
  },
  reporter: {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
  reported: {
    backgroundColor: "#fef2f2",
    color: "#dc2626",
  },
  offensesContainer: {
    marginBottom: theme.spacing(2),
  },
  offensesList: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  offenseChip: {
    backgroundColor: "#fed7d7",
    color: "#c53030",
    fontWeight: 600,
  },
  remarksText: {
    color: theme.palette.text.secondary,
    fontSize: "0.95rem",
    lineHeight: 1.6,
    fontStyle: "italic",
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: "8px",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  statusUpdateSection: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-end",
    marginBottom: theme.spacing(3),
  },
  noteField: {
    marginBottom: theme.spacing(2),
  },
  timeline: {
    marginTop: theme.spacing(2),
  },
  timelineItem: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: "relative",
    paddingLeft: theme.spacing(3),
    "&:before": {
      content: '""',
      position: "absolute",
      left: "0.6rem",
      top: "1.5rem",
      bottom: "-1rem",
      width: "2px",
      backgroundColor: theme.palette.divider,
    },
    "&:last-child:before": {
      display: "none",
    },
  },
  timelineDot: {
    position: "absolute",
    left: 0,
    top: "0.25rem",
    width: "1.2rem",
    height: "1.2rem",
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    border: `2px solid ${theme.palette.background.paper}`,
  },
  timelineDate: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  timelineTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  timelineDescription: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  timelineAdmin: {
    fontSize: "0.8rem",
    color: theme.palette.primary.main,
    fontWeight: 500,
    marginTop: theme.spacing(0.5),
  },
  quickActions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  quickActionButton: {
    justifyContent: "flex-start",
  },
  aiPanel: {
    backgroundColor: "rgba(139, 92, 246, 0.05)",
    border: "1px solid rgba(139, 92, 246, 0.2)",
  },
  aiHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  aiSuggestion: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: "8px",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
}));

function AdminReportManagementPage({ user }) {
  const classes = useStyles();
  const history = useHistory();
  const { reportId } = useParams();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [applyReputationPenalty, setApplyReputationPenalty] = useState(true);
  const [penaltyPercentage, setPenaltyPercentage] = useState(100);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banDays, setBanDays] = useState(7);
  const [banReason, setBanReason] = useState("");
  const [permanentBan, setPermanentBan] = useState(false);

  useEffect(() => {
    if (user && reportId) {
      fetchReport();
    }
  }, [user, reportId]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${basePath}/api/v7/admin/reports/${reportId}/adpass/${user.userId}`,
        {
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.ok) {
        setReport(data.report);
        setNewStatus(data.report.status || "pending");
      } else {
        setError(data.error || "Failed to load report");
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || newStatus === report.status) {
      setSnackbar({ open: true, message: "Please select a different status", severity: "warning" });
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(
        `${basePath}/api/v7/admin/reports/${reportId}/update-status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            status: newStatus,
            note: noteContent || undefined,
            adminUserId: user.userId,
            applyReputationPenalty,
            penaltyPercentage: newStatus === 'resolved' ? penaltyPercentage : undefined,
          }),
        }
      );

      const data = await response.json();

      if (data.ok) {
        const message = newStatus === 'resolved' && applyReputationPenalty
          ? `Status updated & reputation penalty applied (${penaltyPercentage}%)`
          : "Status updated successfully";
        setSnackbar({ open: true, message, severity: "success" });
        setNoteContent("");
        fetchReport(); // Refresh report
      } else {
        setSnackbar({ open: true, message: data.error || "Failed to update status", severity: "error" });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setSnackbar({ open: true, message: "Failed to update status", severity: "error" });
    } finally {
      setUpdating(false);
    }
  };

  const handleBanPlayer = async () => {
    if (!permanentBan && (!banDays || banDays < 1)) {
      setSnackbar({ open: true, message: "Please enter valid ban duration", severity: "warning" });
      return;
    }

    if (!banReason.trim()) {
      setSnackbar({ open: true, message: "Please enter a ban reason", severity: "warning" });
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(
        `${basePath}/api/v7/admin/reports/${reportId}/ban-player`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            days: permanentBan ? 36500 : banDays,
            reason: banReason,
            permanent: permanentBan,
            adminUserId: user.userId,
          }),
        }
      );

      const data = await response.json();

      if (data.ok) {
        setSnackbar({ 
          open: true, 
          message: permanentBan ? "Player permanently banned" : `Player banned for ${banDays} days`, 
          severity: "success" 
        });
        setBanDialogOpen(false);
        setBanDays(7);
        setBanReason("");
        setPermanentBan(false);
        fetchReport(); // Refresh report
      } else {
        setSnackbar({ open: true, message: data.error || "Failed to ban player", severity: "error" });
      }
    } catch (error) {
      console.error("Error banning player:", error);
      setSnackbar({ open: true, message: "Failed to ban player", severity: "error" });
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) {
      setSnackbar({ open: true, message: "Please enter a note", severity: "warning" });
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(
        `${basePath}/api/v7/admin/reports/${reportId}/add-note`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            content: noteContent,
            adminUserId: user.userId,
          }),
        }
      );

      const data = await response.json();

      if (data.ok) {
        setSnackbar({ open: true, message: "Note added successfully", severity: "success" });
        setNoteContent("");
        fetchReport(); // Refresh report
      } else {
        setSnackbar({ open: true, message: data.error || "Failed to add note", severity: "error" });
      }
    } catch (error) {
      console.error("Error adding note:", error);
      setSnackbar({ open: true, message: "Failed to add note", severity: "error" });
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const formatOffenseName = (offense) => {
    return offense.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatStatusLabel = (status) => {
    return status.replace(/_/g, " ").toUpperCase();
  };

  const buildTimeline = () => {
    const timeline = [];
    
    // Add creation event
    timeline.push({
      timestamp: report.createdAt,
      type: "created",
      message: "Report submitted",
    });

    // Add status history
    if (report.statusHistory && Array.isArray(report.statusHistory)) {
      report.statusHistory.forEach((sh) => {
        timeline.push({
          timestamp: sh.timestamp,
          type: "status_change",
          statusFrom: sh.statusFrom,
          statusTo: sh.status,
          adminName: sh.adminName || "Admin",
          message: `Status changed from ${sh.statusFrom || "pending"} to ${sh.status}`,
        });
      });
    }

    // Add admin updates
    if (report.adminUpdates && Array.isArray(report.adminUpdates)) {
      report.adminUpdates.forEach((update) => {
        if (update.updateType === "note") {
          timeline.push({
            timestamp: update.timestamp,
            type: "admin_note",
            adminName: update.adminName || "Admin",
            content: update.content,
          });
        } else if (update.updateType === "status_change") {
          timeline.push({
            timestamp: update.timestamp,
            type: "status_change",
            statusFrom: update.statusFrom,
            statusTo: update.statusTo,
            adminName: update.adminName || "Admin",
            message: `Status changed from ${update.statusFrom} to ${update.statusTo}`,
          });
        }
      });
    }

    // Sort by timestamp (newest first)
    timeline.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return timeline;
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <Typography variant="h5" color="error">
            {error || "Report not found"}
          </Typography>
          <Button
            onClick={() => history.push("/admin")}
            style={{ marginTop: "1rem" }}
          >
            Back to Admin
          </Button>
        </Paper>
      </div>
    );
  }

  const offenses = Object.keys(report.offenses || {}).filter((k) => report.offenses[k]);
  const timeline = buildTimeline();

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <IconButton onClick={() => history.goBack()}>
          <ArrowBackIcon />
        </IconButton>
        <Typography className={classes.title}>
          Report #{reportId.slice(-6)}
        </Typography>
        <Chip
          label={formatStatusLabel(report.status || "pending")}
          className={classes.statusChip}
          color="primary"
        />
      </div>

      <div className={classes.mainContent}>
        {/* Left Column */}
        <div className={classes.leftColumn}>
          {/* Report Details */}
          <Paper className={classes.paper}>
            <Typography className={classes.sectionTitle}>
              <FlagIcon />
              Report Details
            </Typography>

            <Box display="flex" gap={2} mb={2} alignItems="center">
              <EventIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                Submitted: {formatDate(report.createdAt)}
              </Typography>
              <UpdateIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                Updated: {formatDate(report.lastUpdatedAt || report.createdAt)}
              </Typography>
            </Box>

            <div className={classes.matchInfo}>
              <MapIcon color="primary" />
              <Typography style={{ flex: 1 }}>
                <strong>Map:</strong> {report.matchMap || "Unknown"}
              </Typography>
              <Button
                component="a"
                href={`/admin/match/${report.matchId}`}
                target="_blank"
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon />}
              >
                View Match
              </Button>
            </div>

            <div className={classes.playersRow}>
              <div className={classes.playerCard}>
                <Avatar
                  src={report.reporterUserData?.picture}
                  alt={report.reporterUserData?.nickname}
                  className={classes.avatar}
                />
                <Typography variant="h6">
                  {report.reporterUserData?.nickname || "Unknown"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ELO: {report.reporterUserData?.elo || "N/A"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {report.reporterSteamId}
                </Typography>
                <Chip
                  label="REPORTER"
                  className={`${classes.roleLabel} ${classes.reporter}`}
                />
              </div>

              <div className={classes.playerCard}>
                <Avatar
                  src={report.reportedUserData?.picture}
                  alt={report.reportedUserData?.nickname}
                  className={classes.avatar}
                />
                <Typography variant="h6">
                  {report.reportedUserData?.nickname || "Unknown"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ELO: {report.reportedUserData?.elo || "N/A"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {report.reportedSteamId}
                </Typography>
                <Chip
                  label="REPORTED"
                  className={`${classes.roleLabel} ${classes.reported}`}
                />
              </div>
            </div>

            <div className={classes.offensesContainer}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
                Reported Offenses:
              </Typography>
              <div className={classes.offensesList}>
                {offenses.map((offense) => (
                  <Chip
                    key={offense}
                    label={formatOffenseName(offense)}
                    className={classes.offenseChip}
                  />
                ))}
              </div>
            </div>

            {report.remarks && (
              <div>
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
                  Reporter's Description:
                </Typography>
                <Typography className={classes.remarksText}>
                  "{report.remarks}"
                </Typography>
              </div>
            )}
          </Paper>

          {/* Status Update Section */}
          <Paper className={classes.paper}>
            <Typography className={classes.sectionTitle}>
              Update Status
            </Typography>

            <div className={classes.statusUpdateSection}>
              <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newStatus}
                  label="Status"
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <MenuItem value="submitted">Submitted</MenuItem>
                  <MenuItem value="in_review">In Review</MenuItem>
                  <MenuItem value="resolved">Resolved (Validated)</MenuItem>
                  <MenuItem value="concluded">Concluded</MenuItem>
                  <MenuItem value="dismissed">Dismissed (False Report)</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdateStatus}
                disabled={updating || newStatus === report.status}
              >
                Update Status
              </Button>
            </div>

            {newStatus === 'resolved' && (
              <Box style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}>
                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                  <WarningIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Reputation Penalty Settings
                </Typography>
                <Box display="flex" alignItems="center" gap={2} marginBottom={1}>
                  <FormControl size="small" style={{ minWidth: 150 }}>
                    <InputLabel>Penalty Severity</InputLabel>
                    <Select
                      value={penaltyPercentage}
                      label="Penalty Severity"
                      onChange={(e) => setPenaltyPercentage(e.target.value)}
                    >
                      <MenuItem value={25}>25% (Minor)</MenuItem>
                      <MenuItem value={50}>50% (Moderate)</MenuItem>
                      <MenuItem value={75}>75% (Serious)</MenuItem>
                      <MenuItem value={100}>100% (Severe)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>
                    {penaltyPercentage === 100 
                      ? 'Full penalty will be applied based on offense type'
                      : `Reduced penalty (${penaltyPercentage}%) - use for first-time or unclear violations`}
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  ✓ Automatic reputation penalties will be applied to reported player<br/>
                  ✓ Reporter will receive credibility bonus<br/>
                  ✓ Both parties will be notified
                </Typography>
              </Box>
            )}

            {newStatus === 'dismissed' && (
              <Box style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                backgroundColor: 'rgba(251, 191, 36, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(251, 191, 36, 0.3)'
              }}>
                <Typography variant="caption" color="textSecondary">
                  ⚠️ False report penalty will be applied to reporter<br/>
                  ✓ Reported player will be notified that report was dismissed
                </Typography>
              </Box>
            )}

            <TextField
              label="Status Update Note (optional)"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              className={classes.noteField}
              placeholder="Add context about this status change..."
            />
          </Paper>

          {/* Add Note Section */}
          <Paper className={classes.paper}>
            <Typography className={classes.sectionTitle}>
              Add Admin Note
            </Typography>

            <TextField
              label="Note Content"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              className={classes.noteField}
              placeholder="Add notes about investigation, findings, or actions taken..."
            />

            <Button
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              onClick={handleAddNote}
              disabled={updating || !noteContent.trim()}
            >
              Post Note
            </Button>
          </Paper>

          {/* Timeline */}
          <Paper className={classes.paper}>
            <Typography className={classes.sectionTitle}>
              <TimelineIcon />
              Report Timeline
            </Typography>

            <div className={classes.timeline}>
              {timeline.length > 0 ? (
                timeline.map((item, index) => (
                  <div key={index} className={classes.timelineItem}>
                    <div className={classes.timelineDot} />
                    <div>
                      <Typography className={classes.timelineDate}>
                        {formatDate(item.timestamp)}
                      </Typography>
                      {item.type === "created" && (
                        <>
                          <Typography className={classes.timelineTitle}>
                            Report Created
                          </Typography>
                          <Typography className={classes.timelineDescription}>
                            {item.message}
                          </Typography>
                        </>
                      )}
                      {item.type === "status_change" && (
                        <>
                          <Typography className={classes.timelineTitle}>
                            Status Update
                          </Typography>
                          <Typography className={classes.timelineDescription}>
                            {item.message}
                          </Typography>
                          {item.adminName && (
                            <Typography className={classes.timelineAdmin}>
                              By: {item.adminName}
                            </Typography>
                          )}
                        </>
                      )}
                      {item.type === "admin_note" && (
                        <>
                          <Typography className={classes.timelineTitle}>
                            Admin Note
                          </Typography>
                          <Typography className={classes.timelineDescription}>
                            {item.content}
                          </Typography>
                          {item.adminName && (
                            <Typography className={classes.timelineAdmin}>
                              By: {item.adminName}
                            </Typography>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <Typography color="textSecondary">No timeline updates yet</Typography>
              )}
            </div>
          </Paper>
        </div>

        {/* Right Column */}
        <div className={classes.rightColumn}>
          {/* Quick Actions */}
          <Paper className={classes.paper}>
            <Typography className={classes.sectionTitle} variant="h6">
              Quick Actions
            </Typography>
            <div className={classes.quickActions}>
              <Button
                className={classes.quickActionButton}
                startIcon={<OpenInNewIcon />}
                component="a"
                href={`/admin/match/${report.matchId}`}
                target="_blank"
              >
                View Match Details
              </Button>
              <Button
                className={classes.quickActionButton}
                startIcon={<PersonIcon />}
                component="a"
                href={`/admin/profile/${report.reportedUserData?._id}`}
                target="_blank"
              >
                Reported Player History
              </Button>
              <Button
                className={classes.quickActionButton}
                startIcon={<PersonIcon />}
                component="a"
                href={`/admin/profile/${report.reporterUserData?._id}`}
                target="_blank"
              >
                Reporter History
              </Button>
            </div>
          </Paper>

          {/* Ban Actions */}
          <Paper className={classes.paper} style={{ 
            backgroundColor: 'rgba(239, 68, 68, 0.05)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}>
            <Typography className={classes.sectionTitle} variant="h6" style={{ color: '#EF4444' }}>
              <GavelIcon style={{ marginRight: '0.5rem' }} />
              Moderation Actions
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '1rem' }}>
              Apply bans to the reported player
            </Typography>
            <div className={classes.quickActions}>
              <Button
                className={classes.quickActionButton}
                startIcon={<BlockIcon />}
                variant="outlined"
                style={{ 
                  borderColor: '#F97316', 
                  color: '#F97316',
                  justifyContent: 'flex-start'
                }}
                onClick={() => {
                  setBanDialogOpen(true);
                  setPermanentBan(false);
                  setBanDays(7);
                  setBanReason('');
                }}
                disabled={report.banApplied}
              >
                Temporary Ban
              </Button>
              <Button
                className={classes.quickActionButton}
                startIcon={<GavelIcon />}
                variant="outlined"
                style={{ 
                  borderColor: '#DC2626', 
                  color: '#DC2626',
                  justifyContent: 'flex-start'
                }}
                onClick={() => {
                  setBanDialogOpen(true);
                  setPermanentBan(true);
                  setBanReason('Cheating - Permanent ban');
                }}
                disabled={report.banApplied}
              >
                Permanent Ban (Cheating)
              </Button>
              {report.banApplied && (
                <Chip 
                  label="Ban Already Applied" 
                  size="small" 
                  style={{ 
                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                    color: '#DC2626',
                    marginTop: '0.5rem'
                  }} 
                />
              )}
            </div>
          </Paper>

          {/* AI Assistant Panel (Placeholder) */}
          <Paper className={`${classes.paper} ${classes.aiPanel}`}>
            <div className={classes.aiHeader}>
              <ComputerIcon color="primary" />
              <Typography className={classes.sectionTitle} variant="h6" style={{ marginBottom: 0 }}>
                AI Assistant
              </Typography>
            </div>
            <Typography variant="body2" color="textSecondary">
              AI analysis coming soon...
            </Typography>
            <div className={classes.aiSuggestion}>
              <Typography variant="caption" color="textSecondary">
                This feature will provide automated analysis and suggestions for report handling.
              </Typography>
            </div>
          </Paper>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Ban Player Dialog */}
      <Dialog 
        open={banDialogOpen} 
        onClose={() => !updating && setBanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {permanentBan ? 'Permanent Ban' : 'Temporary Ban'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {permanentBan ? (
              <Box 
                style={{ 
                  padding: '1rem', 
                  backgroundColor: 'rgba(220, 38, 38, 0.1)',
                  borderRadius: '8px',
                  border: '2px solid rgba(220, 38, 38, 0.5)'
                }}
              >
                <Typography variant="h6" style={{ color: '#DC2626', fontWeight: 700, marginBottom: '0.5rem' }}>
                  ⚠️ PERMANENT BAN WARNING
                </Typography>
                <Typography variant="body2">
                  This action will permanently ban the player from the platform.
                  Use this only for severe violations like confirmed cheating, hacking, or repeated serious offenses.
                </Typography>
              </Box>
            ) : (
              <FormControl variant="outlined" fullWidth>
                <TextField
                  label="Ban Duration (days)"
                  type="number"
                  value={banDays}
                  onChange={(e) => setBanDays(parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 365 }}
                  variant="outlined"
                />
              </FormControl>
            )}

            <TextField
              label="Ban Reason"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              required
              placeholder={permanentBan 
                ? "e.g., Confirmed cheating with external tools, multiple reports validated" 
                : "e.g., Griefing, toxic behavior, first offense"}
            />

            <Box style={{ 
              padding: '0.75rem', 
              backgroundColor: 'rgba(251, 191, 36, 0.1)',
              borderRadius: '6px'
            }}>
              <Typography variant="caption" color="textSecondary">
                <strong>What happens when you ban:</strong><br/>
                • Player's account status changes to "banned"<br/>
                • Ban is recorded in their ban history<br/>
                • Email notification sent to the player<br/>
                • In-app notification sent<br/>
                • Ban is linked to this report
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBanDialogOpen(false)} 
            disabled={updating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBanPlayer}
            variant="contained"
            style={{ 
              backgroundColor: permanentBan ? '#DC2626' : '#F97316',
              color: 'white'
            }}
            disabled={updating || !banReason.trim() || (!permanentBan && banDays < 1)}
            startIcon={permanentBan ? <GavelIcon /> : <BlockIcon />}
          >
            {updating 
              ? 'Processing...' 
              : permanentBan 
                ? 'Confirm Permanent Ban' 
                : `Ban for ${banDays} ${banDays === 1 ? 'Day' : 'Days'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

AdminReportManagementPage.propTypes = {
  user: PropTypes.object,
};

export default AdminReportManagementPage;
